import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState } from "react";

/**
 * The Platform Terms, read without leaving the screen that asks for agreement.
 *
 * Shared by every clickwrap — signup's profile step and the campaign checkout's confirm dialog —
 * which is why it sits under `components/legal/` and not under either flow. Both surfaces must
 * show the same document the same way; a second copy would be free to drift from the first.
 *
 * An <iframe> over the stored PDF, not a rendered copy: the bytes shown here are the bytes the
 * acceptance hashes, which is the whole point of keeping one canonical object. A second rendering
 * — extracted text, an HTML twin — would be a second source of truth free to drift from the
 * document the client actually agreed to.
 *
 * WHY THE FALLBACK IS NOT OPTIONAL. iOS Safari does not render a PDF in an iframe; it shows an
 * empty box or offers a download, and a client who cannot read the terms cannot meaningfully agree
 * to them. So the new-tab link is always present rather than conditional on sniffing the browser,
 * and it reports back as "opened" exactly as reading it here does.
 */
export function TermsDocumentDialog({
  open,
  url,
  version,
  onClose,
}: {
  open: boolean;
  url: string;
  version: string;
  onClose: () => void;
}) {
  // An iframe fires `load` for a rendered PDF and, in browsers that refuse to render one, often
  // never fires at all — so this drives a hint rather than a blocking state. Nothing depends on it
  // except the sentence under the frame.
  const [loaded, setLoaded] = useState(false);
  const fullScreen = useMediaQuery(useTheme().breakpoints.down("sm"));

  return (
    // As large as the window allows. A contract set in a small frame is read by scrolling a
    // paragraph at a time, which is how someone ends up agreeing to a document they skimmed — and
    // the frame is the only thing standing between the client and the terms, so it gets the room.
    // Full screen below `sm`: on a phone a centred box inside a centred box is all margin.
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      slotProps={{ paper: { sx: { height: fullScreen ? "100%" : "94vh", maxHeight: "94vh" } } }}
    >
      <DialogTitle sx={{ fontFamily: "var(--font-crimson)", fontSize: 20, pb: 1 }}>
        Gifts for Good Platform Terms &amp; Conditions
        <Typography component="span" sx={{ fontSize: 13, color: "var(--color-graphite)", ml: 1 }}>
          ({version})
        </Typography>
      </DialogTitle>

      {/* flex:1 rather than a viewport height: the frame takes whatever the title and actions leave,
          so it fills a tall window instead of guessing at a fraction of one. */}
      <Box sx={{ px: 3, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Box
          component="iframe"
          src={url}
          title="Gifts for Good Platform Terms & Conditions"
          onLoad={() => setLoaded(true)}
          sx={{
            width: "100%",
            flex: 1,
            minHeight: 0,
            border: "1px solid var(--color-card-border)",
            borderRadius: "8px",
            bgcolor: "var(--color-wax)",
          }}
        />
        {!loaded && (
          <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", mt: 1 }}>
            If the document does not appear, open it in a new tab.
          </Typography>
        )}
      </Box>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Button
          href={url}
          target="_blank"
          rel="noreferrer"
          size="small"
          sx={{ color: "var(--color-dust-ink)" }}
        >
          Open in a new tab
        </Button>
        <Button variant="contained" onClick={onClose} sx={{ bgcolor: "var(--color-rodeo-black)" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
